/**
 * InstanceList — shared component backing all 4 /processes/instances pages.
 *
 * ActiveInstances / CompletedInstances / FailedInstances / InstanceMonitoring
 * each fetch the same endpoint and filter by `status`. Rather than keeping
 * 4 copies of the table/dialog/actions, each page becomes a 20-line wrapper
 * that declares its status filter and its unique summary-card set.
 *
 * Backend ProcessInstance has a unified shape covering every status
 * (running/waiting/suspended/completed/failed/cancelled), so filtering
 * client-side against `statusFilter` keeps the endpoint calls simple.
 *
 * Actions are per ROW, gated on that row's own status: Cancel on
 * running/waiting/suspended, Retry on failed, view detail always. There is no
 * per-page allowlist — four wrappers used to pass one, and its only effect was to
 * hide an action a row was otherwise entitled to (svdy-ui §12.8: per-cell intent).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { STATUS_GROUPS, type StatusGroup } from '../ProcessInstances/statusGroups';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress,
  FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select,
  Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  CancelOutlined, DeleteOutlineOutlined, Refresh as RefreshIcon,
  ReplayOutlined, Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { ApiError, organizationApi, processesApi } from '../../../api';
import type { ProcessInstance } from '../../../api/processes';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../components/primitives';
import { getProcessDefinitionDisplay, translateLiveStatus, translatePriority } from '../processDisplay';
import { translateApiError } from '../../../i18n/translateApiError';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InstanceSummaryCard {
  id: string;
  title: string;
  value: string;
  description?: string;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
}

export interface InstanceListProps {
  title: string;
  description: string;
  /**
   * Status is a FILTER on this page, not four pages.
   *
   * There used to be `statuses` and `allowedActions` props, and four 40-line
   * wrappers that differed only in what they passed — with `InstanceMonitoring`
   * passing all statuses and both actions, i.e. the superset of the other three.
   * The per-row action gates below already read each row's own status, so
   * `allowedActions` could only ever show FEWER actions than a row warranted.
   *
   * `initialStatusGroup` lets the old `/processes/instances/active` URL land on
   * the right filter instead of 404ing (svdy-ui §12.10).
   */
  initialStatusGroup?: StatusGroup;
  /** Optional summary card renderer — called with the filtered list. */
  buildSummary?: (rows: ProcessInstance[]) => InstanceSummaryCard[];
  /** Optional guidance when this status-scoped view has no rows. */
  emptyDescription?: string;
  emptyAction?: { label: string; path: string };
  /** A primary action for the page header. Added 2026-09-05 for "file a request for a worker" — the page
   *  had no action slot, and giving one component a slot beats a second page chrome (svdy-ui §1). */
  headerAction?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

function statusColor(s: string | null | undefined): 'success' | 'info' | 'warning' | 'error' | 'default' {
  if (s === 'completed' || s === 'approved') return 'success';
  if (s === 'running') return 'info';
  if (s === 'waiting' || s === 'suspended' || s === 'withdrawn') return 'warning';
  if (s === 'failed' || s === 'cancelled' || s === 'rejected') return 'error';
  return 'default';
}

function priorityColor(p: string | null | undefined): 'error' | 'warning' | 'info' | 'default' {
  if (p === 'critical' || p === 'urgent' || p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'info';
  return 'default';
}

function currentNodeLabel(instance: ProcessInstance): string {
  if (!instance.current_node_id || !instance.flow_snapshot || typeof instance.flow_snapshot !== 'object') return '—';
  const nodes = (instance.flow_snapshot as { nodes?: Array<{ id?: string; data?: { label?: string } }> }).nodes;
  return nodes?.find((node) => node.id === instance.current_node_id)?.data?.label ?? '—';
}

function currentAssigneeName(instance: ProcessInstance, workerNames: Map<string, string>): string {
  // Once it is over the instance's assignee is null (`engine._complete_instance`), so the question changes
  // from "who owes an action" to "who decided" — and only the timeline still knows.
  if (isTerminal(instance)) return decidedByName(instance) ?? '—';
  return instance.current_assignee?.name
    ?? (instance.current_assignee_id ? workerNames.get(instance.current_assignee_id) : undefined)
    ?? '—';
}

/** Statuses where the request is over. Nothing is pending on anybody. */
const TERMINAL_STATUSES = ['completed', 'rejected', 'cancelled', 'withdrawn', 'failed'];

/** Ended WITHOUT anyone ruling on it — the only statuses that may offer Delete.
 *
 * Mirrors `UNDECIDED_INSTANCE_STATUSES` in `app/processes/service.py`, and is deliberately a
 * whitelist: a status added later (say `expired`) must default to NOT offering deletion. Note
 * `failed` is absent — an engine failure on a request somebody submitted is an incident record,
 * and the server would refuse it anyway. */
const TERMINAL_UNDECIDED_STATUSES = ['withdrawn', 'cancelled'];

function isTerminal(instance: ProcessInstance): boolean {
  return TERMINAL_STATUSES.includes(instance.status ?? '');
}

/**
 * The step a request is waiting at — or, once it is over, the step it ENDED at.
 *
 * THREE ROUNDS, and the third is the one that works (owner, 2026-08-31 and again 2026-09-09: the column
 * was empty on all four rows).
 *
 * Round 1: the raw `current_node_id` label. `engine.withdraw_instance` clears the assignee but not the
 * node, and the completed/rejected paths did the same, so a finished request read "Current Step: Approval"
 * with a named person beside it — someone still owes an action on something nobody can act on.
 *
 * Round 2: returning "—" fixed the lie and created a new one. Current Step and Assignee sat side by side
 * as two empty dashes, which reads as MISSING DATA rather than as "nothing is pending".
 *
 * Round 2 then WROTE DOWN a fix it did not implement. Its docstring said "a terminal row states where it
 * stopped, MUTED" and that `current_node_id` is what marks where a request stopped — and
 * `engine._complete_instance` sets `current_node_id = None` AND `current_assignee_id = None`. The data is
 * DESTROYED at completion, not merely unrendered, so reading it could only ever produce the dash the owner
 * screenshotted. A comment describing behaviour the code cannot have is worse than no comment: it stops
 * the next reader from looking.
 *
 * Round 3 derives the answer from the TIMELINE, which the list response already carries (eager-loaded, see
 * `service.list_instances`) — no new field, no backend round trip. Start and end nodes are skipped because
 * "ended at End" is not an answer; what a reader wants from a rejected request is the step that rejected
 * it, and from a completed one, the last thing that actually happened.
 */
function endedAtStepLabel(instance: ProcessInstance): string | undefined {
  const nodes = (instance.flow_snapshot as {
    nodes?: Array<{ id?: string; type?: string; data?: { label?: string } }>;
  } | null)?.nodes;
  if (!Array.isArray(nodes)) return undefined;
  const entered = (instance.timeline ?? []).filter((event) => event.action === 'node_entered');
  for (let i = entered.length - 1; i >= 0; i -= 1) {
    const node = nodes.find((candidate) => candidate.id === entered[i].node_id);
    const type = (node?.type ?? '').toLowerCase();
    if (!node || type === 'start' || type === 'end') continue;
    if (node.data?.label) return node.data.label;
  }
  return undefined;
}

/** Who actually decided it, for a request that is over. The instance's own assignee is nulled at
 *  completion, so the only surviving answer is the actor on the last decision event. */
function decidedByName(instance: ProcessInstance): string | undefined {
  const decisions = (instance.timeline ?? []).filter(
    (event) => event.action === 'task_approved' || event.action === 'task_rejected',
  );
  const last = decisions[decisions.length - 1] as { actor_name?: string | null } | undefined;
  return last?.actor_name ?? undefined;
}

function currentStepLabel(instance: ProcessInstance): string {
  if (isTerminal(instance)) return endedAtStepLabel(instance) ?? '—';
  return currentNodeLabel(instance);
}

/** WHO ASKED — the first question anyone asks of an approval table, and the column it lacked.
 *
 * Reads the nested `requester` before the worker-name map, because the map is loaded with
 * `status='active'`: a request filed by someone who has since left resolves through the nested
 * actor and would otherwise be blank in exactly this column. */
function requesterName(instance: ProcessInstance, workerNames: Map<string, string>): string {
  return instance.requester?.name
    ?? (instance.requested_by ? workerNames.get(instance.requested_by) : undefined)
    ?? '—';
}

function fmtDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

function initials(name: string | null | undefined): string {
  if (!name) return '•';
  return name.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const InstanceList: React.FC<InstanceListProps> = ({
  headerAction,
  title,
  description,
  initialStatusGroup = 'all',
  buildSummary,
  emptyDescription,
  emptyAction,
}) => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<ProcessInstance[]>([]);
  const [definitionNames, setDefinitionNames] = useState<Map<string, string>>(new Map());
  const [workerNames, setWorkerNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [statusGroup, setStatusGroup] = useState<StatusGroup>(initialStatusGroup);

  const [filter, setFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'urgent' | 'high' | 'medium' | 'low'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  const [pendingCancel, setPendingCancel] = useState<ProcessInstance | null>(null);
  /** Only offered on a request that ENDED WITHOUT A DECISION — see `handleConfirmDelete`. */
  const [pendingDelete, setPendingDelete] = useState<ProcessInstance | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Instance rows are the page's source of truth. Definition/worker names
      // are optional decorations and must never blank the table when either
      // lookup is temporarily unavailable (especially after a cancellation).
      const data = await processesApi.processInstancesApi.list();
      setRows(data);

      const [definitionsResult, workersResult] = await Promise.allSettled([
        processesApi.processDefinitionsApi.list(),
        organizationApi.workersApi.list('active'),
      ]);
      if (definitionsResult.status === 'fulfilled') {
        setDefinitionNames(new Map(
          definitionsResult.value.map((definition) => [
            definition.id,
            getProcessDefinitionDisplay(t, definition).name,
          ]),
        ));
      }
      if (workersResult.status === 'fulfilled') {
        setWorkerNames(new Map(
          workersResult.value.map((worker) => [
            worker.id,
            [worker.first_name, worker.last_name].filter(Boolean).join(' ') || worker.worker_code,
          ]),
        ));
      }
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('processes:instances.loadError'));
      // Preserve the last known rows. A failed refresh must not turn a real
      // list into a false empty state.
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const scoped = useMemo(
    () => rows.filter((r) => STATUS_GROUPS[statusGroup].includes(r.status ?? '')),
    [rows, statusGroup],
  );

  /** `?definitionId=` — a deep link INTO this list, scoped to one process.
   *
   * Added 2026-09-01 for the delete refusal: "requests on this process are still going through
   * approval" is only actionable if the admin can find them, and the process is the one thing they
   * know. Filtered client-side because this list already fetches the org's instances and filters
   * every other dimension here (svdy-ui §12.3 — group filters below ~100 rows belong on the
   * client, not in the query string of an API).
   *
   * Read from `window.location.search` rather than a router hook because this component is mounted
   * by several wrappers and one of them renders outside a `<Routes>` subtree; a hook would throw
   * there. The value is only ever compared, never rendered. */
  const [definitionIdFilter, setDefinitionIdFilter] = useState<string | null>(() => {
    const raw = new URLSearchParams(window.location.search).get('definitionId');
    return raw?.trim() || null;
  });

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return scoped.filter((r) => {
      if (definitionIdFilter && r.definition_id !== definitionIdFilter) return false;
      if (priorityFilter !== 'all' && (r.priority ?? '') !== priorityFilter) return false;
      if (!q) return true;
      return (
        (r.instance_number ?? r.id).toLowerCase().includes(q) ||
        currentNodeLabel(r).toLowerCase().includes(q) ||
        (definitionNames.get(r.definition_id) ?? '').toLowerCase().includes(q) ||
        requesterName(r, workerNames).toLowerCase().includes(q) ||
        currentAssigneeName(r, workerNames).toLowerCase().includes(q)
      );
    });
  }, [scoped, filter, priorityFilter, definitionNames, workerNames, definitionIdFilter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const summary = useMemo(
    () => (buildSummary ? buildSummary(scoped) : []),
    [buildSummary, scoped],
  );

  /** Remove a request that never produced a decision.
   *
   * The row-level gate below is `withdrawn` / `cancelled` only, and it is NOT the real gate: a
   * request can be withdrawn AFTER a first approver signed off, and the server refuses that with
   * `process_instance_has_decision` because the signature is evidence. So this button is an
   * invitation, not a promise, and the 409 has to be shown as the reason rather than swallowed as
   * a generic failure — which is why the catch translates `detail.code`.
   */
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await processesApi.processInstancesApi.remove(pendingDelete.id);
      setRows((prev) => prev.filter((row) => row.id !== pendingDelete.id));
      setToast(t('processes:instances.deleteToast', {
        number: pendingDelete.instance_number ?? pendingDelete.id.slice(0, 8),
      }));
      setPendingDelete(null);
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('processes:instances.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!pendingCancel) return;
    try {
      const cancelled = await processesApi.processInstancesApi.cancel(pendingCancel.id);
      // Apply the mutation locally before refreshing. Active views remove only
      // this row; all-status views keep it with its new cancelled state.
      setRows((current) => current.map((row) => (
        row.id === cancelled.id ? cancelled : row
      )));
      setToast(t('processes:instances.cancelToast', { number: pendingCancel.instance_number ?? pendingCancel.id.slice(0, 8) }));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('processes:instances.cancelFailed'));
      throw e;
    }
  };

  const handleRetry = async (inst: ProcessInstance) => {
    try {
      await processesApi.processInstancesApi.retry(inst.id);
      setToast(t('processes:instances.retryToast', { number: inst.instance_number ?? inst.id.slice(0, 8) }));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('processes:instances.retryFailed'));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {headerAction}
          <Tooltip title={t('common:actions.refresh')}>
            <span>
              <IconButton size="small" onClick={fetchAll} disabled={loading}
                sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {summary.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
          {summary.flatMap((s, i) => [
            i > 0 ? <Typography key={`sep-${s.id}`} variant="caption" color="text.disabled">·</Typography> : null,
            <Typography key={s.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="strong" sx={{ color: `${s.color ?? 'primary'}.main`, fontWeight: 700 }}>{s.value}</Box>
              {s.title}
            </Typography>,
          ]).filter(Boolean)}
        </Stack>
      )}

      {/* Filter bar — FILTER_DESIGN_CONVENTION §3. Process instances are
          org-scoped (not geo-scoped), so no Location filter. */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="instance-status-group">{t('processes:instances.statusLabel')}</InputLabel>
          <Select
            labelId="instance-status-group"
            value={statusGroup}
            label={t('processes:instances.statusLabel')}
            onChange={(e) => { setStatusGroup(e.target.value as StatusGroup); setPage(0); }}
          >
            {(Object.keys(STATUS_GROUPS) as StatusGroup[]).map((group) => (
              <MenuItem key={group} value={group}>
                {t(`processes:instances.statusGroups.${group}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>{t('processes:instances.priorityLabel')}</InputLabel>
          <Select value={priorityFilter} label={t("processes:instances.priorityLabel")}
            onChange={(e) => { setPriorityFilter(e.target.value as typeof priorityFilter); setPage(0); }}>
            <MenuItem value="all">{t("processes:instances.allPriorities")}</MenuItem>
            <MenuItem value="urgent">{t("processes:instances.urgent")}</MenuItem>
            <MenuItem value="high">{t("processes:instances.high")}</MenuItem>
            <MenuItem value="medium">{t("processes:instances.medium")}</MenuItem>
            <MenuItem value="low">{t("processes:instances.low")}</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" variant="outlined" label={t("processes:instances.searchPlaceholder")} value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 280 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
      </Box>

      {/* Active filter chips — §5 */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        // THE PROCESS FILTER, FIRST and VISIBLE.
        //
        // It arrives from a `?definitionId=` deep link — the delete refusal's "see these requests".
        // Without a chip the admin lands on a short list with no explanation and no way back to the
        // full one, which is worse than not filtering at all: they cannot tell a filter from an
        // empty product. Reported 2026-09-01: "filter应该是可以在前端的filter中显示的，不然如何定位".
        //
        // First in the row because it is the widest scope — Where → Who → What (svdy-ui §4), and a
        // process is the "where" of a request.
        if (definitionIdFilter) {
          chips.push({
            key: 'definition',
            label: t('processes:requests.processChip', {
              // The NAME, not the id. `definitionNames` is already loaded for the table's column, so
              // the chip costs no extra fetch; the id is the fallback for a definition since deleted.
              value: definitionNames.get(definitionIdFilter) ?? definitionIdFilter,
            }),
            onClear: () => { setDefinitionIdFilter(null); setPage(0); },
          });
        }
        if (priorityFilter !== 'all') {
          chips.push({ key: 'prio',
            label: t('processes:tasks.priorityChip', { value: translatePriority(t, priorityFilter) }),
            onClear: () => { setPriorityFilter('all'); setPage(0); } });
        }
        if (filter.trim()) {
          chips.push({ key: 'search', label: t('processes:requests.searchChip', { value: filter.trim() }),
            onClear: () => { setFilter(''); setPage(0); } });
        }
        if (chips.length === 0) return null;
        return (
          <Box sx={{ width: '100%', display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chips.map((c) => (
              <Chip key={c.key} label={c.label} size="small" onDelete={c.onClear}
                variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }} />
            ))}
            <Button size="small"
              onClick={() => {
                setDefinitionIdFilter(null); setPriorityFilter('all'); setFilter(''); setPage(0);
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}>
              {t('processes:requests.clearAll')}
            </Button>
          </Box>
        );
      })()}

      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        {/* minWidth so the container SCROLLS instead of the cells wrapping. Without it a
          table narrower than its content breaks headers onto two lines and gives every
          row a different height — the raggedness the owner reported on Roles, found in
          six more tables by the module audit. 650 is what 9 of the 12 sized admin
          tables use. */}
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t("processes:instances.colInstance")}</TableCell>
              <TableCell>{t("processes:instances.colRequester")}</TableCell>
              <TableCell>{t("processes:instances.colCurrentStep")}</TableCell>
              <TableCell>{t("processes:instances.colAssignee")}</TableCell>
              <TableCell align="center">{t("processes:instances.colPriority")}</TableCell>
              <TableCell align="center">{t("processes:instances.colStatus")}</TableCell>
              <TableCell>{t("processes:instances.colStarted")}</TableCell>
              <TableCell align="center">{t("processes:instances.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <Stack spacing={0.75} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('processes:instances.noInstances')}
                    </Typography>
                    {emptyDescription && (
                      <Typography variant="caption" color="text.disabled">
                        {emptyDescription}
                      </Typography>
                    )}
                    {emptyAction && (
                      <Button
                        size="small"
                        onClick={() => navigate(emptyAction.path)}
                        sx={{ mt: 0.5, textTransform: 'none' }}
                      >
                        {emptyAction.label}
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ) : paginated.map((inst) => (
              <TableRow
                key={inst.id}
                hover
                // The row's own cursor, plus one-line cells. Merged rather than a second sx.
                sx={{ cursor: 'pointer', '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}
                onClick={() => navigate(`/processes/instances/${inst.id}`)}
              >
                {/* The prose column: bounded so it wraps at a sane measure instead of
                        stretching the row. */}
                <TableCell sx={{ maxWidth: 320, whiteSpace: 'normal' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {inst.instance_number ?? inst.id.slice(0, 12)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {definitionNames.get(inst.definition_id) ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: 'grey.500' }}>
                      {initials(requesterName(inst, workerNames))}
                    </Avatar>
                    <Typography variant="caption">{requesterName(inst, workerNames)}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  {/* Muted once the request is over: the step it ended at is history, not a
                    * queue. Paired with an empty Assignee cell and the Status chip beside it,
                    * that reads as "it stopped here" rather than "someone owes an action". */}
                  <Typography
                    variant="body2"
                    color={isTerminal(inst) ? 'text.disabled' : 'text.primary'}
                  >
                    {currentStepLabel(inst)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {/* THREE STATES, not two. Round 2 gated this cell on `!isTerminal(inst)`, so a terminal
                      row rendered the dash whatever `currentAssigneeName` returned — which made the
                      function's terminal branch unreachable and is why the column was empty on every
                      history row (owner, 2026-09-09). No AVATAR on a terminal row: an avatar reads as
                      somebody who owes an action, and the decision is already made. */}
                  {isTerminal(inst) ? (
                    <Typography variant="caption" color="text.disabled">
                      {currentAssigneeName(inst, workerNames)}
                    </Typography>
                  ) : currentAssigneeName(inst, workerNames) !== '—' ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: 'primary.main' }}>
                        {initials(currentAssigneeName(inst, workerNames))}
                      </Avatar>
                      <Typography variant="caption">{currentAssigneeName(inst, workerNames)}</Typography>
                    </Stack>
                  ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell align="center">
                  <Chip label={translatePriority(t, inst.priority)} size="small" color={priorityColor(inst.priority)}
                    variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell align="center">
                  <Chip label={translateLiveStatus(t, inst.status)} size="small" color={statusColor(inst.status)}
                    variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{fmtDate(inst.started_at ?? inst.created_at, i18n.language)}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title={t('processes:tasks.viewDetails')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/processes/instances/${inst.id}`);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {['running', 'waiting', 'suspended'].includes(inst.status) && (
                      <Tooltip title={t('processes:instances.cancelInstance')}>
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setPendingCancel(inst); }}>
                          <CancelOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {TERMINAL_UNDECIDED_STATUSES.includes(inst.status) && (
                      <Tooltip title={t('processes:instances.deleteInstance')}>
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setPendingDelete(inst); }}>
                          <DeleteOutlineOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {inst.status === 'failed' && (
                      <Tooltip title={t('processes:instances.retry')}>
                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleRetry(inst); }}>
                          <ReplayOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={!!pendingCancel}
        tone="danger"
        title={t('processes:instances.cancelInstance')}
        description={t('processes:instances.cancelDesc')}
        subject={pendingCancel && (
          <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
            {pendingCancel.instance_number ?? pendingCancel.id.slice(0, 12)}
          </Box>
        )}
        confirmLabel={t('processes:instances.cancelInstance')}
        confirmLoadingLabel={t('processes:instances.cancellingLabel')}
        onConfirm={handleConfirmCancel}
        onClose={() => setPendingCancel(null)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('processes:instances.deleteInstance')}
        description={t('processes:instances.deleteDesc')}
        subject={pendingDelete && (
          <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
            {pendingDelete.instance_number ?? pendingDelete.id.slice(0, 12)}
          </Box>
        )}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('processes:instances.deletingLabel')}
        onConfirm={handleConfirmDelete}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InstanceList;

// Re-export so wrappers can import helpers if needed.
export { priorityColor, statusColor, fmtDate };
